import { useState, useEffect, useRef } from 'react';
import { useStepNavigation } from '../../hooks/useStepNavigation';
import StepIndicator from '../../components/StepIndicator';
import StepNavigation from '../../components/StepNavigation';
import { examesService, type ExameItem, type ExameColaboradorItem } from '../../services/exames.service';
import PageContainer from '../../components/PageContainer';
import PageHeader from '../../components/PageHeader';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { limparRascunhoAgendamento } from '../../utils/agendamentoStorage';

interface Colaborador {
  id: string;
  nome: string;
  cpf: string;
  exames: ExameColaboradorItem[];
  loading: boolean;
}

export default function DetalhesPage() {
  const { empresa } = useEmpresa();
  const { currentStep, stepData, saveStepData, goToNextStep, goToPreviousStep } = useStepNavigation();
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [examesDisponiveis, setExamesDisponiveis] = useState<ExameItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const prevEmpresaIdRef = useRef<number | null>(empresa?.id ?? null);

  useEffect(() => {
    if (!empresa?.id) return;
    const currentEmpresaId = empresa.id.toString();
    const savedEmpresaId = localStorage.getItem('customer_employeesData_empresa_id');

    const empresaMudou = prevEmpresaIdRef.current !== null && prevEmpresaIdRef.current !== empresa.id;
    const rascunhoDivergente = Boolean(savedEmpresaId && savedEmpresaId !== currentEmpresaId);

    if (empresaMudou || rascunhoDivergente) {
      console.log('🔄 [Detalhes] Empresa alterada durante agendamento. Limpando rascunho e redirecionando para início.');
      limparRascunhoAgendamento();
      window.location.href = '/';
      return;
    }
    prevEmpresaIdRef.current = empresa.id;
  }, [empresa?.id]);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      const savedEmpresaId = localStorage.getItem('customer_employeesData_empresa_id');
      const currentEmpresaId = empresa?.id ? empresa.id.toString() : localStorage.getItem('customer_empresa_id');

      if (savedEmpresaId && currentEmpresaId && savedEmpresaId !== currentEmpresaId) {
        console.warn('⚠️ [Detalhes] Empresa divergente. Limpando rascunho e redirecionando.');
        limparRascunhoAgendamento();
        window.location.href = '/';
        return;
      }

      // Carregar colaboradores do stepData
      if (!stepData.employeesData || stepData.employeesData.length === 0) {
        window.location.href = '/agendamento/cadastro';
        return;
      }

      const colaboradoresComExames = stepData.employeesData.map((col: Record<string, unknown>) => ({
        ...col,
        exames: [],
        loading: false,
      })) as unknown as Colaborador[];

      setColaboradores(colaboradoresComExames);

      // Carregar exames disponíveis
      const exames = await examesService.getExamesDisponiveis();
      setExamesDisponiveis(exames);

      // Carregar exames de cada colaborador
      await loadExamesColaboradores(colaboradoresComExames);

    } catch (err: unknown) {
      console.error('Erro ao carregar dados:', err);
      const e = err as { message?: string };
      setError(e?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadExamesColaboradores = async (colaboradoresList: Colaborador[]) => {
    for (const colaborador of colaboradoresList) {
      try {
        if (!colaborador.id.startsWith('local_')) {
          const exames = await examesService.getExamesPorColaborador(parseInt(colaborador.id));
          setColaboradores(prev =>
            prev.map(col =>
              col.id === colaborador.id
                ? { ...col, exames }
                : col
            )
          );
        }
      } catch (error) {
        console.error(`Erro ao carregar exames do colaborador ${colaborador.id}:`, error);
      }
    }
  };

  const handleToggleExame = async (colaboradorId: string, exameId: number) => {
    const colaborador = colaboradores.find(c => c.id === colaboradorId);
    if (!colaborador) return;

    const isSelected = colaborador.exames.some(e => e.id === exameId);

    if (isSelected) {
      await handleDesvincularExame(colaboradorId, exameId);
    } else {
      await handleVincularExame(colaboradorId, exameId);
    }
  };

  const handleVincularExame = async (colaboradorId: string, exameId: number) => {
    if (colaboradorId.startsWith('local_')) {
      alert('Por favor, salve o colaborador primeiro antes de adicionar exames.');
      return;
    }

    setColaboradores(prev =>
      prev.map(col =>
        col.id === colaboradorId
          ? { ...col, loading: true }
          : col
      )
    );

    try {
      await examesService.vincularExameColaborador(parseInt(colaboradorId), exameId);

      // Remover dados de exames do localStorage após integrar
      try {
        localStorage.removeItem('customer_examsData');
        saveStepData({ examsData: undefined });
        console.log('🧹 [Detalhes] Dados de exames removidos do localStorage após vincular exame');
      } catch (error) {
        console.warn('⚠️ [Detalhes] Erro ao limpar dados de exames do localStorage:', error);
      }

      // Recarregar exames do colaborador
      const exames = await examesService.getExamesPorColaborador(parseInt(colaboradorId));
      setColaboradores(prev =>
        prev.map(col =>
          col.id === colaboradorId
            ? { ...col, exames, loading: false }
            : col
        )
      );

    } catch (err: unknown) {
      console.error('Erro ao vincular exame:', err);
      const e = err as { message?: string };
      setError(e?.message || 'Erro ao adicionar exame');
      setColaboradores(prev =>
        prev.map(col =>
          col.id === colaboradorId
            ? { ...col, loading: false }
            : col
        )
      );
    }
  };

  const handleDesvincularExame = async (colaboradorId: string, exameId: number) => {
    if (colaboradorId.startsWith('local_')) {
      return;
    }

    setColaboradores(prev =>
      prev.map(col =>
        col.id === colaboradorId
          ? { ...col, loading: true }
          : col
      )
    );

    try {
      await examesService.desvincularExameColaborador(parseInt(colaboradorId), exameId);

      // Recarregar exames do colaborador
      const exames = await examesService.getExamesPorColaborador(parseInt(colaboradorId));
      setColaboradores(prev =>
        prev.map(col =>
          col.id === colaboradorId
            ? { ...col, exames, loading: false }
            : col
        )
      );

    } catch (err: unknown) {
      console.error('Erro ao remover exame:', err);
      const e = err as { message?: string };
      setError(e?.message || 'Erro ao remover exame');
      setColaboradores(prev =>
        prev.map(col =>
          col.id === colaboradorId
            ? { ...col, loading: false }
            : col
        )
      );
    }
  };

  const handleContinuar = async () => {
    setSaving(true);
    try {
      // Salvar dados dos exames selecionados
      saveStepData({ examsData: colaboradores });
      goToNextStep();
    } catch (err: unknown) {
      console.error('Erro ao salvar:', err);
      setError('Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };



  const getFilteredExames = () => {
    if (!searchTerm) return examesDisponiveis;
    return examesDisponiveis.filter(exame =>
      exame.nome.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Seleção de Exames Laboratoriais"
        description="Escolha os exames que cada colaborador irá realizar"
      />

        {/* Step indicator */}
        <StepIndicator currentStep={currentStep} />

        {/* Step navigation */}
        <StepNavigation
          currentStep={currentStep}
          canGoNext={true}
          canGoPrevious={true}
          onNext={handleContinuar}
          onPrevious={goToPreviousStep}
          loading={saving}
        />

        {/* Informações adicionais */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 mt-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            💡 Sobre a seleção de exames
          </h3>
          <ul className="text-blue-800 text-sm space-y-1">
            <li>• Você pode revisar os exames posteriormente no resumo</li>
            <li>• Os exames marcados como "online" podem ser realizados remotamente</li>
            <li>• O faturamento será consolidado na fatura mensal do contrato</li>
          </ul>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Erro na operação
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p className="whitespace-pre-line">{error}</p>
                </div>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError('')}
                  className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
                >
                  <span className="sr-only">Fechar</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}



        {/* Lista de colaboradores */}
        <div className="space-y-8">
          {colaboradores.map((colaborador) => (
            <div key={colaborador.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{colaborador.nome}</h3>
                  <p className="text-gray-600">CPF: {colaborador.cpf}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Exames selecionados</p>
                  <p className="text-xl font-bold text-green-600">
                    {colaborador.exames.length}
                  </p>
                </div>
              </div>

              <div className="p-6">
                {/* Filtro */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar Exame
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Digite o nome do exame..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Grid de Exames */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {getFilteredExames().map((exame) => {
                    const isSelected = colaborador.exames.some(e => e.exame_id === exame.id);
                    return (
                      <div
                        key={exame.id}
                        onClick={() => !colaborador.loading && handleToggleExame(colaborador.id, exame.id)}
                        className={`
                          relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
                          ${isSelected
                            ? 'border-green-500 bg-green-50 shadow-md'
                            : 'border-gray-200 hover:border-green-300 hover:shadow-sm bg-white'
                          }
                          ${colaborador.loading ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className={`font-semibold ${isSelected ? 'text-green-900' : 'text-gray-900'}`}>
                              {exame.nome}
                            </h4>
                          </div>
                          <div className={`
                            w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-3
                            ${isSelected
                              ? 'bg-green-500 border-green-500 text-white'
                              : 'border-gray-300'
                            }
                          `}>
                            {isSelected && (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
    </PageContainer>
  );
}

